import React from 'react';
import type { ReviewItem } from '../../lib/adminReviewApi';
import {
  AutoGateStatusBadge,
  DeliveryStatusBadge,
  ManualReviewStatusBadge,
  ScanStatusBadge,
  WorkflowStatusBadge,
} from './AdminReviewStatusBadge';

interface AdminReviewListProps {
  items: ReviewItem[];
  selectedUploadId?: string | null;
  onSelect: (uploadId: string) => void;
  loading?: boolean;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const AdminReviewList: React.FC<AdminReviewListProps> = ({
  items,
  selectedUploadId,
  onSelect,
  loading,
}) => {
  if (loading) {
    return (
      <div className="card text-sm text-secondary-600 dark:text-secondary-300">
        Loading review items…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="card text-center text-sm text-secondary-600 dark:text-secondary-300">
        <p className="font-medium text-secondary-900 dark:text-white">No review items yet</p>
        <p className="mt-2">Run Discover Dry-Run or Create Missing from the discover panel.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200 dark:border-secondary-700">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200 text-sm dark:divide-secondary-700">
          <thead className="bg-secondary-50 dark:bg-secondary-800">
            <tr className="text-left text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
              <th className="px-3 py-2">Upload</th>
              <th className="px-3 py-2">Opponent</th>
              <th className="px-3 py-2">Workflow</th>
              <th className="px-3 py-2">Delivery</th>
              <th className="px-3 py-2">Auto-gate</th>
              <th className="px-3 py-2">Scans</th>
              <th className="px-3 py-2">Reviewer</th>
              <th className="px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-200 bg-white dark:divide-secondary-700 dark:bg-secondary-900">
            {items.map((item) => {
              const selected = item.upload_id === selectedUploadId;
              return (
                <tr
                  key={item.upload_id}
                  onClick={() => onSelect(item.upload_id)}
                  className={`cursor-pointer transition-colors hover:bg-primary-50 dark:hover:bg-secondary-800 ${
                    selected ? 'bg-primary-50 dark:bg-secondary-800' : ''
                  }`}
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    <div className="max-w-[10rem] truncate" title={item.upload_id}>
                      {item.upload_id}
                    </div>
                    <div className="mt-1 text-secondary-500 dark:text-secondary-400">
                      {item.analysis_quality || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{item.opponent_gamertag || '—'}</div>
                    <div className="text-xs text-secondary-500 dark:text-secondary-400">
                      {item.opponent_team || '—'} · {item.owner_team || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <WorkflowStatusBadge status={item.workflow_status} />
                    <div className="mt-1">
                      <ManualReviewStatusBadge status={item.manual_review_status} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <DeliveryStatusBadge status={item.delivery_status} />
                  </td>
                  <td className="px-3 py-2">
                    <AutoGateStatusBadge status={item.auto_gate_status} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <ScanStatusBadge
                        status={item.projection_scan_status}
                        count={item.projection_scan_hits_count}
                      />
                      <ScanStatusBadge
                        status={item.forbidden_scan_status}
                        count={item.forbidden_scan_hits_count}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">{item.reviewer || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatTimestamp(item.updated_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminReviewList;
